<template>
    <connection-settings-popup
        :is-new="isNew"
        :comment-id="commentId"
        :invalid="$v.$invalid"
        :resources="{
            deletePromptTitle: resource('JourneyBuilder_TimerSettings_Delete_Title'),
            deletePromptBody: resource('JourneyBuilder_TimerSettings_Delete_Body')
        }"

        @save="save()"
        @delete="deleteCheckpoint()"
        @display-errors="displayErrors()"
        @hidden="$emit('hidden')"
    >
        <template #title>
            <h3 class="d-inline-block">
                {{resource('JourneyBuilder_TimerSettings_Title')}}
            </h3>

            <h4 class="mt-1 ml-1">
                <help-popover
                    :title="resource('JourneyBuilder_TimerSettings_Title')"
                    :content="resource('JourneyBuilder_TimerSettings_Description')"
                />
            </h4>
        </template>
        
        <template #body>
            <div class="row form-group">
                <div class="col-12">
                    <label>{{resource('JourneyBuilder_TimerSettings_Body_Title')}}</label>
                </div>
            </div>

            <div class="row form-group">
                <div class="col-6">
                    <settings-property>
                        <template #label="{id}">
                            <label :for="id">{{ resource('JourneyBuilder_TimerSettings_ScheduleType_Label') }}</label>
                        </template>
                        
                        <template #input="{id}">
                            <v-select
                                class="form-control"

                                v-model="scheduleTypeModel"
                                :options="SCHEDULE_TYPE_OPTIONS"
                                :disabled="!canWrite"
                                :clearable="false"

                                :input-id="id"

                                searchable
                                filterable
                            />
                        </template>
                        
                        <template #errors>
                            <required-error-message v-if="$v.$dirty && $v.scheduleType.$invalid" />
                        </template>
                    </settings-property>
                </div>
            </div>
            
            <brick-timer-schedule-once-settings 
                v-if="scheduleTypeModel === SCHEDULE_TYPES.ONCE"
                
                ref="onceSettings"
                
                :date.sync="singleRunConfiguration.date"
                :time.sync="singleRunConfiguration.time"
            />

            <template v-if="scheduleTypeModel === SCHEDULE_TYPES.RECURRING">
                <brick-timer-frequency-settings
                    ref="frequencySettings"
                    
                    :unit.sync="frequencyConfiguration.unit"
                    :recurs-in-days.sync="frequencyConfiguration.recursInDays"
                    :weekdays.sync="frequencyConfiguration.weekdays"
                />
                
                <brick-timer-daily-frequency-settings
                    ref="dailyFrequencySettings"
                    
                    :frequency.sync="dailyFrequencyConfiguration.frequency"
                    :recurrence-unit.sync="dailyFrequencyConfiguration.recurrenceUnit"
                    :recurs-in-seconds.sync="dailyFrequencyConfiguration.recursInSeconds"
                    :start-time.sync="dailyFrequencyConfiguration.startTime"
                    :end-time.sync="dailyFrequencyConfiguration.endTime"
                />
                
                <brick-timer-duration-settings
                    ref="durationSettings"
                    
                    :end-date.sync="durationConfiguration.endDate"
                    :start-date.sync="durationConfiguration.startDate"
                    :is-perpetual.sync="durationConfiguration.isPerpetual"
                />
            </template>
            
            <div class="row form-group">
                <div class="col-12">
                    <hr />
                </div>
            </div>
            
            <div class="row">
                <div class="col-12">
                    <p v-html="summary" />
                </div>
            </div>
        </template>
    </connection-settings-popup>
</template>

<script>
import { timerCheckpoint } from "@modules/JourneyBuilder/Bricks/Checkpoints";
import dateHelpers from "@modules/JourneyBuilder/Util/date-helpers";
import { 
    timerScheduleOnceValidations, 
    timerFrequencyValidations, 
    buildTimerDailyFrequencyValidatorSet, 
    timerDurationValidations
} from "./validations";

import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import permissionsMixin from '@modules/JourneyBuilder/Mixins/journey-permissions';
import resourceMixin from "@core/Util/Mixins/resources";

import BrickTimerScheduleOnceSettings from "./BrickTimerScheduleOnceSettings";
import BrickTimerFrequencySettings from "./BrickTimerFrequencySettings";
import BrickTimerDailyFrequencySettings from "./BrickTimerDailyFrequencySettings";
import BrickTimerDurationSettings from "./BrickTimerDurationSettings";

import ConnectionSettingsPopup from '../ConnectionSettingsPopup';
import SettingsProperty from "../SettingsProperty";
import { HelpPopover } from "@modules/JourneyBuilder/Components/Elements";
import RequiredErrorMessage from "@core/Components/Errors/RequiredErrorMessage";
import VSelect from 'vue-select';

/**
 * Wrap a set of validators to only actually run when the given schedule type is selected
 * @param {Object} scheduleType
 * @param {Object} validatorSet
 * @return {Object}
 */
const asScheduleTypeDependentValidatorSet = (scheduleType, validatorSet) => {
    const scheduleTypeDependentValidators = {};
    
    const makeValidatorsScheduleTypeDependent = (source, target) => {
        Object.keys(source).forEach(key => {
            const value = source[key];
            const isValidator = typeof value === 'function';
            const isValidatorSet = typeof value === 'object';
            
            if(isValidator) {
                target[key] = asScheduleTypeDependentValidator(value);
            } else if (isValidatorSet) {
                target[key] = {};
                makeValidatorsScheduleTypeDependent(value, target[key]);
            } else {
                console.warn(`Unsupported type: ${typeof value} for property ${key} in`, source);
            }
        });
    }

    const asScheduleTypeDependentValidator = concreteValidator => {
        return function() {
            //Validator may be ignored for the currently selected schedule type
            if(this.scheduleType !== scheduleType.id) {
                return true;
            }

            return concreteValidator.apply(this, arguments);
        }
    }
    
    makeValidatorsScheduleTypeDependent(validatorSet, scheduleTypeDependentValidators);
    
    return scheduleTypeDependentValidators;
};

const createBaseConfiguration = () => ({
    frequencyConfiguration: {
        recursInDays: undefined,
        unit: undefined, //week, day, etc.

        weekdays: [] //Can only be specified when working with weeks
    },

    dailyFrequencyConfiguration: {
        frequency: undefined,
        recurrenceUnit: undefined,
        recursInSeconds: undefined,

        startTime: { hour: undefined, minute: undefined, second: undefined },
        endTime: { hour: undefined, minute: undefined, second: undefined }
    },

    durationConfiguration: {
        startDate: undefined,
        endDate: undefined,
        isPerpetual: false
    },

    singleRunConfiguration: {
        date: undefined,
        time: { hour: undefined, minute: undefined, second: undefined }
    }
})

const SCHEDULE_TYPES = timerCheckpoint.SCHEDULE_TYPES;
const SCHEDULE_TYPE_OPTIONS = Object.values(SCHEDULE_TYPES);

export default {
    name: "BrickTimerSettings",
    
    components: {
        RequiredErrorMessage,
        BrickTimerScheduleOnceSettings,
        BrickTimerFrequencySettings,
        BrickTimerDailyFrequencySettings,
        BrickTimerDurationSettings,
        
        ConnectionSettingsPopup,
        SettingsProperty,
        HelpPopover,
        VSelect
    },
    
    mixins: [ validationMixin, permissionsMixin, resourceMixin ],
    
    props: {
        config: Object,
        connection: Object,
        isNew: Boolean
    },
    
    data() {
        return {
            version: 2,
            
            SCHEDULE_TYPE_OPTIONS,
            SCHEDULE_TYPES,

            scheduleType: undefined,

            ...createBaseConfiguration()
        }
    },
    
    computed: {
        commentId() {
            return "connection:" + this.connection.id + ":timer";
        },
        
        scheduleTypeModel: {
            get() {
                return SCHEDULE_TYPE_OPTIONS.find(option => option.id === this.scheduleType);
            },
            set(value) {
                this.scheduleType = value.id;
            }
        },
        
        summary() {
            return timerCheckpoint.generateSummary(this.configuration);
        },
        
        configuration() {
            const serializeTime = time => {
                if(
                    time.hour === undefined &&
                    time.minute === undefined &&
                    time.second === undefined
                ) {
                    return undefined;
                }
                
                const hourPart = (time.hour || 0).toString().padStart(2, '0');
                const minutePart = (time.minute || 0).toString().padStart(2, '0');
                const secondPart = (time.second || 0).toString().padStart(2, '0');

                return `${hourPart}:${minutePart}:${secondPart}`;
            };
            
            //Split our data in two objects - one actual configuration, and one for storing UI data?
            const configuration = {
                version: this.version,
                
                UI: { },
                scheduleType: this.scheduleType
            };

            if(this.scheduleType === SCHEDULE_TYPES.ONCE.id) {
                configuration.date = this.singleRunConfiguration.date;
                configuration.time = serializeTime(this.singleRunConfiguration.time);
            }

            if(this.scheduleType === SCHEDULE_TYPES.RECURRING.id) {
                configuration.UI.frequencyRecurrenceUnit = this.frequencyConfiguration.unit;
                configuration.UI.dailyFrequencyRecurrenceUnit = this.dailyFrequencyConfiguration.recurrenceUnit;

                configuration.recursInDays = this.frequencyConfiguration.recursInDays;
                configuration.weekdays = this.frequencyConfiguration.weekdays;

                configuration.dailyFrequency = this.dailyFrequencyConfiguration.frequency;
                configuration.dailyStartTime = serializeTime(this.dailyFrequencyConfiguration.startTime);
                configuration.dailyEndTime = serializeTime(this.dailyFrequencyConfiguration.endTime);
                configuration.dailyRecursInSeconds = this.dailyFrequencyConfiguration.recursInSeconds;

                //IsPerpetual can be inferred based on an endTime being set or not
                configuration.startDate = this.durationConfiguration.startDate;
                configuration.endDate = this.durationConfiguration.endDate;
            }
            
            return configuration;
        }
    },
    
    validations: {
        scheduleType: {
            required
        },

        singleRunConfiguration: asScheduleTypeDependentValidatorSet(SCHEDULE_TYPES.ONCE, timerScheduleOnceValidations),
        frequencyConfiguration: asScheduleTypeDependentValidatorSet(SCHEDULE_TYPES.RECURRING, timerFrequencyValidations),
        dailyFrequencyConfiguration: asScheduleTypeDependentValidatorSet(SCHEDULE_TYPES.RECURRING, buildTimerDailyFrequencyValidatorSet(vm => vm.dailyFrequencyConfiguration)),
        durationConfiguration: asScheduleTypeDependentValidatorSet(SCHEDULE_TYPES.RECURRING, timerDurationValidations)
    },
    
    created() {
        const configuration = this.config[timerCheckpoint.name];

        const parseTime = time => {
            //V2 time may be undefined when not used in config type
            if(time === undefined) {
                return {
                    hour: undefined,
                    minute: undefined,
                    second: undefined
                }
            }
            
            //V1 exposed our internal format to external parties
            //we can simply use the given time
            if(configuration.version === 1) {
                return { ...time };
            }
            
            //V2 converts our time object to a string representation
            //Convert from hh:mm:ss -> internal object representation
            const [
                hourPart,
                minutePart,
                secondPart
            ] = time.split(':');

            return {
                hour: parseInt(hourPart),
                minute: parseInt(minutePart),
                second: parseInt(secondPart)
            };
        };
        
        this.scheduleType = configuration.scheduleType;
        
        if(configuration.scheduleType === SCHEDULE_TYPES.ONCE.id) {
            this.singleRunConfiguration.date = dateHelpers.convertConfigToInternalFormat(configuration.date);
            this.singleRunConfiguration.time = parseTime(configuration.time);
        }

        if(configuration.scheduleType === SCHEDULE_TYPES.RECURRING.id) {
            this.frequencyConfiguration.unit = configuration.UI.frequencyRecurrenceUnit;
            this.dailyFrequencyConfiguration.recurrenceUnit = configuration.UI.dailyFrequencyRecurrenceUnit;
            
            this.frequencyConfiguration.recursInDays = configuration.recursInDays;
            this.frequencyConfiguration.weekdays = configuration.weekdays;

            this.dailyFrequencyConfiguration.frequency = configuration.dailyFrequency;
            this.dailyFrequencyConfiguration.startTime = parseTime(configuration.dailyStartTime);
            this.dailyFrequencyConfiguration.endTime = parseTime(configuration.dailyEndTime);
            this.dailyFrequencyConfiguration.recursInSeconds = configuration.dailyRecursInSeconds;

            //IsPerpetual can be inferred based on an endTime being set or not
            this.durationConfiguration.startDate = dateHelpers.convertConfigToInternalFormat(configuration.startDate);
            this.durationConfiguration.endDate = dateHelpers.convertConfigToInternalFormat(configuration.endDate);
            this.durationConfiguration.isPerpetual = !configuration.endDate;
        }
    },
    
    watch: {
        scheduleTypeModel(_, oldValue) {
            //Guard against resetting our configuration on initialization
            if(!oldValue) {
                return;
            }
            
            //Type switched -> reset our complete configuration
            const baseConfiguration = createBaseConfiguration();
            
            Object.keys(baseConfiguration)
                .forEach(baseConfigKey => this[baseConfigKey] = baseConfiguration[baseConfigKey]);
        }
    },
    
    methods: {
        save() {
            //Fix date formats
            const configuration = { ...this.configuration };
            
            if(configuration.startDate) configuration.startDate = dateHelpers.convertInternalToConfigFormat(configuration.startDate);
            if(configuration.endDate) configuration.endDate = dateHelpers.convertInternalToConfigFormat(configuration.endDate);
            if(configuration.date) configuration.date = dateHelpers.convertInternalToConfigFormat(configuration.date);
            
            this.$emit("save", {
                [timerCheckpoint.name]: {
                    version: this.version, //Make it easier to upgrade at a later stage
                    ...configuration
                }
            });
        },

        deleteCheckpoint() {
            this.$emit('delete-connection-checkpoint', { connection: this.connection, checkpoint: timerCheckpoint.name });
        },

        displayErrors(){
            this.$v.$touch();
            
            if(this.$refs.onceSettings) { 
                this.$refs.onceSettings.displayErrors(); 
            }
            
            if(this.$refs.frequencySettings) { 
                this.$refs.frequencySettings.displayErrors(); 
            }
            
            if(this.$refs.dailyFrequencySettings) { 
                this.$refs.dailyFrequencySettings.displayErrors(); 
            }
            
            if(this.$refs.durationSettings) { 
                this.$refs.durationSettings.displayErrors(); 
            }
        }
    }
}
</script>
